import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CardStateRefused,
  ErrorCode,
  InvalidFormField,
  MarketNotLinked,
  SealedProductNotFound,
  StoreNotFound,
  type StoreRoleForbidden,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { StockLevel } from "../model/card";
import { parse } from "../model/common";
import { MarketPrice, MarketRefresh } from "../model/market";
import {
  type NewSealedProduct,
  SealedProduct,
  type SealedProductListFilter,
  type SealedProductPatch,
  type SealedStockUpdate,
} from "../model/sealed";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

const SealedProductListResponse = z.object({ products: z.array(SealedProduct) });
const SuccessResponse = z.object({ success: z.literal(true) });

/** `/shop/:shopId/sealed`: the boxes, cases and tins the shop breaks. */
export class SealedApi {
  constructor(private readonly t: Transport) {}

  async list(
    ctx: ApiContext,
    shopId: string,
    query: { filter?: SealedProductListFilter; search?: string } = {},
  ): Promise<Result<SealedProduct[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/sealed`, { query: { ...query } });
    return forward(
      r,
      (j) => parse(SealedProductListResponse, j).products,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async create(
    ctx: ApiContext,
    shopId: string,
    product: NewSealedProduct,
  ): Promise<Result<SealedProduct, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField>> {
    const r = await this.t.post(ctx, `/shop/${shopId}/sealed`, { body: product });
    return forward(
      r,
      (j) => parse(SealedProduct, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  async update(
    ctx: ApiContext,
    shopId: string,
    sealedProductId: string,
    patch: SealedProductPatch,
  ): Promise<
    Result<
      SealedProduct,
      ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | InvalidFormField
    >
  > {
    const r = await this.t.patch(ctx, `/shop/${shopId}/sealed/${sealedProductId}`, { body: patch });
    return forward(
      r,
      (j) => parse(SealedProduct, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "sealedProductId") return new SealedProductNotFound(sealedProductId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Sets the total on hand. Refused below what live breaks are holding. */
  async setStock(
    ctx: ApiContext,
    shopId: string,
    sealedProductId: string,
    update: SealedStockUpdate,
  ): Promise<
    Result<
      StockLevel,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | SealedProductNotFound
      | CardStateRefused
      | InvalidFormField
    >
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/sealed/${sealedProductId}/stock`, { body: update });
    return forward(
      r,
      (j) => parse(StockLevel, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "sealedProductId") return new SealedProductNotFound(sealedProductId, e);
          if (e.code === ErrorCode.IncorrectState) return new CardStateRefused(e.detailedDesc, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** The shared market estimate and its history, the same in every shop holding the product. */
  async marketPrice(
    ctx: ApiContext,
    shopId: string,
    sealedProductId: string,
  ): Promise<Result<MarketPrice, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound>> {
    const r = await this.t.get(ctx, `/shop/${shopId}/sealed/${sealedProductId}/market-price`);
    return forward(
      r,
      (j) => parse(MarketPrice, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "sealedProductId") return new SealedProductNotFound(sealedProductId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Asks the pricing source for the market estimate, past the price cache.
   * Within a minute of the last retrieval the outcome is `cooldown` and nothing
   * is asked. The shop's own prices are untouched.
   */
  async refreshMarketPrice(
    ctx: ApiContext,
    shopId: string,
    sealedProductId: string,
  ): Promise<
    Result<
      MarketRefresh,
      ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | MarketNotLinked
    >
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/sealed/${sealedProductId}/market-price/refresh`);
    return forward(
      r,
      (j) => parse(MarketRefresh, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.code === ErrorCode.NotLinked) return new MarketNotLinked(e.detailedDesc, e);
          if (e.incorrectValue === "sealedProductId") return new SealedProductNotFound(sealedProductId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Retires a product. Needs zero on hand. */
  async archive(
    ctx: ApiContext,
    shopId: string,
    sealedProductId: string,
  ): Promise<
    Result<void, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | SealedProductNotFound | CardStateRefused>
  > {
    const r = await this.t.post(ctx, `/shop/${shopId}/sealed/${sealedProductId}/archive`);
    return forward(
      r,
      (j) => {
        parse(SuccessResponse, j);
      },
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(shopId, e);
          if (e.incorrectValue === "sealedProductId" && e.code === ErrorCode.DoesntExist) {
            return new SealedProductNotFound(sealedProductId, e);
          }
          if (e.incorrectValue === "sealedProductId") return new CardStateRefused(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
